import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { DbUser, Courses, CourseStructureCtx } from "../../../../interfaces";
import { UserManagementModal } from "../UserManagementModal";
import { AlertState } from "../../../../types";
import { TechnicalIssuesMessage } from "../../utils/Toast";
import { CustomCheckBox } from "../../utils/CustomCheckBox";
import { CourseEditState, useCourseEditState } from "../state/useCourseEditState";
import { useCallback, useContext } from "react";
import { makeStyles } from "@mui/styles";
import { v2theme } from "../../../../styles/Theme";
import axios from "../../../../services/axios";
import { CourseStructureContext } from "../../../../context/CourseStructureContext";
import { CourseMetadata } from "@backend-types";

export type EditCourseProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (alert: AlertState) => void;
  currentUser: DbUser;
  course: Courses;
};

export type CourseRequest = Pick<Courses, "id" | "name">;

const useStyle = makeStyles((theme) => ({
  inputText: {
    "& .MuiOutlinedInput-root": {
      "&:hover fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: v2theme.palette.primary.main,
      },
    },
  },
}));

export function EditCourse(props: EditCourseProps) {
  const classes = useStyle();
  const [state, dispatch] = useCourseEditState(props);
  const courseStructureCtx = useContext(CourseStructureContext) as CourseStructureCtx;

  const updateForm = useCallback(
    (update: Partial<CourseEditState["form"]>) =>
      dispatch({ type: "updateForm", update }),
    [dispatch]
  );

  // Normalize undefined or null values to empty strings to ensure consistent comparison.
  // This prevents false positives in formModified() when props metadata fields are missing (undefined)
  // but form state initializes them as empty strings ("").
  const normalize = (value: any) => (value == null ? "" : value);
  const normalizeBool = (v: any) => Boolean(v);

  const normalizeGradingFromMetadata = (value?: string) => ({
    gradingMethod: value === "completion" ? "completion" : "accuracy",
    scoreType: value === "completion" ? "" : value ?? "",
  });

  const formModified = () => {
    const grading = normalizeGradingFromMetadata(
      props.course.metadata?.lti_grading_method
    );
    return !(
      normalize(state.form.deepLinkAssetType) ===
        normalize(props.course.metadata?.deeplink_asset_type) &&
      normalize(state.form.gradingMethod) === normalize(grading.gradingMethod) &&
      normalize(state.form.scoreType) === normalize(grading.scoreType) &&
      normalizeBool(state.form.isModuleLocked) ===
        normalizeBool(props.course.metadata?.use_digit_study_tool) &&
      normalizeBool(state.form.showModuleOrder) ===
        normalizeBool(props.course.metadata?.show_module_order) &&
      normalize(state.form.assessmentMetricsType) ===
        normalize(props.course.metadata?.management_page?.assessment_metrics_type) &&
      normalizeBool(state.form.showSubmoduleProgressBar) ===
        normalizeBool(props.course.metadata?.management_page?.show_submodule_progress)
    );
  };

  const handleClose = () => {
    props.onClose();
  };

  const submit = async () => {
    const { form } = state;

    const data: Partial<Record<string, any>> = {};

    // Backend shallow-merges metadata; nested objects must be fully merged here to avoid losing keys
    const existingManagementPageObject = props.course.metadata?.management_page ?? {};
    if (
      normalize(form.deepLinkAssetType) !==
      normalize(props?.course?.metadata?.deeplink_asset_type)
    ) {
      data.deeplink_asset_type = form.deepLinkAssetType;
    }

    if (
      normalize(form.gradingMethod) !==
      normalize(props?.course?.metadata?.lti_grading_method)
    ) {
      if (form.gradingMethod === "accuracy") data.lti_grading_method = form.scoreType;
      else data.lti_grading_method = form.gradingMethod;
    }

    if (form.isModuleLocked !== props?.course?.metadata?.use_digit_study_tool) {
      data.use_digit_study_tool = form.isModuleLocked;
    }

    if (form.showModuleOrder !== props?.course?.metadata?.show_module_order) {
      data.show_module_order = form.showModuleOrder;
    }

    if (
      form.assessmentMetricsType !==
      props?.course?.metadata?.management_page?.assessment_metrics_type
    ) {
      data.management_page = {
        ...existingManagementPageObject,
        assessment_metrics_type: form.assessmentMetricsType,
      };
    }

    if (
      form.showSubmoduleProgressBar !==
      props?.course?.metadata?.management_page?.show_submodule_progress
    ) {
      data.management_page = {
        ...existingManagementPageObject,
        ...data.management_page,
        show_submodule_progress: form.showSubmoduleProgressBar,
      };
    }

    if (Object.keys(data).length === 0) {
      props.onSubmit({
        severity: "info",
        message: "No changes detected.",
      });
      return;
    }
    try {
      const res = await axios.put<Courses>(`/course/${props.course.id}`, data);
      if (res.data) {
        courseStructureCtx.updateCourseMetadata(
          props.course.id,
          res.data.metadata as CourseMetadata
        );
      }

      props.onSubmit({
        severity: "success",
        message: `Edited Course`,
      });
    } catch (e: any) {
      props.onSubmit({
        severity: "error",
        message: TechnicalIssuesMessage(),
      });

      throw e;
    }
  };

  return (
    <UserManagementModal
      title="Edit Course"
      open={props.open}
      onClose={handleClose}
      onSave={submit}
      disableSubmit={!formModified()}>
      <Typography color="textSecondary" sx={{ marginBottom: "2rem" }}>
        Please enter the course details.
      </Typography>

      <Grid container spacing={6}>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            value={state.form.deepLinkAssetType}
            onChange={(deepLinkAssetType) => {
              updateForm({ deepLinkAssetType: deepLinkAssetType.target.value });
            }}
            label="Deep Link Type"
            select
            fullWidth>
            {[
              { key: "Module", value: "module" },
              { key: "Target", value: "target" },
            ].map((asset) => (
              <MenuItem key={asset.key} value={asset.value}>
                {asset.key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            value={state.form.gradingMethod}
            onChange={(gradingMethod) => {
              updateForm({ gradingMethod: gradingMethod.target.value });
            }}
            label="Grading Method"
            select
            fullWidth>
            {[
              { key: "Completion", value: "completion" },
              { key: "Accuracy", value: "accuracy" },
            ].map((gradingMethod) => (
              <MenuItem key={gradingMethod.key} value={gradingMethod.value}>
                {gradingMethod.key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ marginTop: "24px" }}>
            <CustomCheckBox
              label={"Module Locking"}
              checked={state.form.isModuleLocked}
              onChange={(e) => updateForm({ isModuleLocked: e.target.checked })}
            />
          </Box>
        </Grid>
        {state.form.gradingMethod === "accuracy" && (
          <Grid item xs={6}>
            <TextField
              className={classes.inputText}
              value={state.form.scoreType}
              onChange={(scoreType) => {
                updateForm({ scoreType: scoreType.target.value });
              }}
              label="Score Type"
              select
              fullWidth>
              {[
                { key: "Points", value: "graded-quizzes-points" },
                { key: "Average", value: "graded-quizzes-average" },
              ].map((scoreType) => (
                <MenuItem key={scoreType.key} value={scoreType.value}>
                  {scoreType.key}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid item xs={6}>
          <Box sx={{ marginTop: "24px" }}>
            <CustomCheckBox
              label={"Module Order"}
              checked={state.form.showModuleOrder}
              onChange={(e) => updateForm({ showModuleOrder: e.target.checked })}
            />
          </Box>
        </Grid>
        <Grid item xs={6}>
          <TextField
            className={classes.inputText}
            value={state.form.assessmentMetricsType}
            onChange={(metricsType) => {
              updateForm({ assessmentMetricsType: metricsType.target.value });
            }}
            label="Assessment Metrics"
            select
            fullWidth>
            {[
              { key: "Activity Type Count", value: "activity-type-count" },
              { key: "Question Count", value: "question-type-count" },
            ].map((metricsType) => (
              <MenuItem key={metricsType.key} value={metricsType.value}>
                {metricsType.key}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6}>
          <Box sx={{ marginTop: "24px" }}>
            <CustomCheckBox
              label={"Show submodule progress bar"}
              checked={state.form.showSubmoduleProgressBar}
              onChange={(e) => updateForm({ showSubmoduleProgressBar: e.target.checked })}
            />
          </Box>
        </Grid>
      </Grid>
    </UserManagementModal>
  );
}
